import { useCallback, useState } from 'react'

export interface UnsavedChanges {
  /** True while any section has unsaved content. */
  hasUnsavedChanges: boolean
  /** Whether a specific section (e.g. 'files', 'website', 'profileText') is unsaved. */
  isSectionUnsaved: (key: string) => boolean
  /** A section reports its unsaved state; keyed so sections don't clobber each other. */
  reportUnsaved: (key: string, value: boolean) => void
  /** Clear every section (e.g. after a confirmed discard / navigation). */
  clearAll: () => void
}

/**
 * Settings-area "unsaved changes" registry, shared across the profile tabs.
 * Each editor/uploader reports its own section; the page reads the aggregate to
 * drive a single nav blocker + tab-switch guards. Foundation for the unified Save.
 */
export function useUnsavedChanges(): UnsavedChanges {
  const [sections, setSections] = useState<Record<string, boolean>>({})

  const hasUnsavedChanges = Object.values(sections).some(Boolean)

  const isSectionUnsaved = useCallback(
    (key: string) => Boolean(sections[key]),
    [sections],
  )

  const reportUnsaved = useCallback((key: string, value: boolean) => {
    setSections((prev) =>
      prev[key] === value ? prev : { ...prev, [key]: value },
    )
  }, [])

  const clearAll = useCallback(() => setSections({}), [])

  return { hasUnsavedChanges, isSectionUnsaved, reportUnsaved, clearAll }
}
